import { useTranslation } from 'next-i18next'
import { useCallback, useEffect, useState } from 'react'
import { useRouter } from 'next/router'

import playcanvasConfig from '~/config/playcanvas'
import playCanvasAnalytics from '~/analytics/playcanvas/playcanvasAnalyticsEventLogger'
import { getServerlessImageHandlerUrl } from '~/components/Image/helpers'
import ROUTES from '~/shared/constants/routes.const'
import { getFirebase } from '~/firebase'
import useCurrentUser from '~/hooks/useCurrentUser'
import HeaderNavbar from '~/math-vault/components/HeaderNavbar'
import { useSetCraneGameDataMutation, useSetCraneGameShipStateMutation } from '~/redux/apis/crane-game-api'
import { CompletedLevel, CraneGameLevelData, ShipLocation, ShipState } from '~/games/types/games.types'
import GameWrapper from '~/games/components/GameWrapper'
import { useSetRewardsMutation } from '~/redux/apis/reward-api'
import { getTotalGemCount } from '~/utils/helpers'
import { REWARD_EVENT, REWARD_FEATURE } from '~/gamification/types/gamification.types'

enum DataTypes {
  REQUEST = 'request',
  REWARDS = 'rewards',
  LANGUAGE = 'language',
  SAVE_DATA = 'saveData',
  AVATAR_DATA = 'avatarData',
  EVENT = 'event',
  PLAYCANVAS_ANALYTICS_EVENT = 'playcanvasAnalyticsEvent',
  LEVEL_DATA = 'levelData',
  SHIP_STATE = 'shipState',
}
enum EventDataTypes {
  HIDE = 'hide topbar',
  SHOW = 'show topbar',
}

export function StacknLoad(): React.ReactElement {
  const { t } = useTranslation('games')
  const user = useCurrentUser()
  const [setCraneGameData] = useSetCraneGameDataMutation<CraneGameLevelData>()
  const [setShipState] = useSetCraneGameShipStateMutation<ShipState>()
  const [setRewards] = useSetRewardsMutation()

  const parentDocId = `${'crane-game'}/${user?.id}`
  const srcKey = user ? user.avatarPngUrl : null
  const avatarImgUrl = srcKey
    ? getServerlessImageHandlerUrl({
        key: srcKey,
        edits: { resize: { fit: 'cover', width: 100, height: 100 } },
      })
    : ''

  const router = useRouter()
  const language = router.locale

  const [isReady, setIsReady] = useState(false)
  const [showTopbar, setShowTopbar] = useState(true)
  const [shipLocation, setShipLocation] = useState<ShipLocation | undefined>()
  const [craneIframe, setCraneIframe] = useState<HTMLIFrameElement | undefined>()
  const [completedLevels, setCompletedLevels] = useState<Record<string, CompletedLevel> | undefined>()

  useEffect(() => {
    const fetchMetadata = async (): Promise<void> => {
      try {
        const { query, getDoc, doc, fireStore, collection, getDocs } = await getFirebase()
        const shipPos = await getDoc(doc(fireStore, parentDocId))

        const completedLevelQuery = query(collection(fireStore, `${parentDocId}/completed_levels`))
        const querySnapshot = await getDocs(completedLevelQuery)
        querySnapshot.forEach((level) => {
          const levelName = level.id
          const metadata = level.data()
          setCompletedLevels((completedLevels) => ({ ...completedLevels, [levelName]: metadata })) //eslint-disable-line
        })

        setShipLocation(shipPos.data() as ShipLocation)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('error:', e)
      }
    }
    if (user?.id) fetchMetadata()
  }, [parentDocId, user])

  useEffect(() => {
    setCraneIframe(document.getElementById('craneIframe') as HTMLIFrameElement)
  }, [setCraneIframe])

  useEffect(() => {
    const postMessage = craneIframe?.contentWindow
    if (isReady && postMessage) {
      if (language) {
        postMessage.postMessage(
          {
            type: DataTypes.LANGUAGE,
            language,
          },
          playcanvasConfig.stacknloadGameURL.origin,
        )
      }

      if (shipLocation || completedLevels) {
        postMessage.postMessage(
          {
            type: DataTypes.SAVE_DATA,
            shipState: {
              x: shipLocation?.shipPosX,
              z: shipLocation?.shipPosZ,
              rotation: shipLocation?.shipRotation,
            },
            completedLevels,
          },
          playcanvasConfig.stacknloadGameURL.origin,
        )
      }

      if (avatarImgUrl) {
        postMessage.postMessage(
          { type: DataTypes.AVATAR_DATA, avatar: avatarImgUrl },
          playcanvasConfig.stacknloadGameURL.origin,
        )
      }
    }
  }, [isReady, craneIframe, shipLocation, completedLevels, avatarImgUrl, language])

  const messageHandler = useCallback(
    (event) => {
      if (event.origin === playcanvasConfig.stacknloadGameURL.origin) {
        // check that the message is coming from playcanvas
        const data = event.data

        if (data.type === DataTypes.REQUEST) {
          setIsReady(true)
        }

        if (data.type === DataTypes.REWARDS) {
          if (
            data.rewards.kudosOrbs ||
            data.rewards.siteScore ||
            data.rewards.gemRed ||
            data.rewards.gemGreen ||
            data.rewards.gemBlue
          ) {
            setRewards({
              gems: getTotalGemCount(data.rewards.gemRed, data.rewards.gemGreen, data.rewards.gemBlue),
              kudos: data.rewards.kudosOrbs,
              xp: data.rewards.siteScore,
              feature: REWARD_FEATURE.GAME_STACKN_LOAD,
              event_id: `${REWARD_FEATURE.GAME_STACKN_LOAD}#${REWARD_EVENT.REWARDS}`,
            })
          }
        }

        if (data.type === DataTypes.EVENT) {
          if (data.event === EventDataTypes.HIDE) {
            setShowTopbar(false)
          } else if (data.event === EventDataTypes.SHOW) {
            setShowTopbar(true)
          }
        }

        if (data.type === DataTypes.PLAYCANVAS_ANALYTICS_EVENT) {
          playCanvasAnalytics.logPlayCanvasEvent(data)
        }

        if (data.type === DataTypes.LEVEL_DATA && data.levelData) {
          const levelData = data.levelData as CraneGameLevelData
          setCraneGameData({
            levelName: levelData.levelName,
            boxesDestroyed: levelData.boxesDestroyed,
            boxesLifted: levelData.boxesLifted,
            date: levelData.date,
            goldBoxesGotten: levelData.goldBoxesGotten,
            specialBoxCount: levelData.specialBoxCount,
          })
        }
        if (data.type === DataTypes.SHIP_STATE && data.shipState) {
          const shipState = data.shipState as ShipState
          setShipState({
            shipPosX: shipState.x,
            shipPosZ: shipState.z,
            shipRotation: shipState.rotation,
          })
        }
      }
    },
    [setIsReady, setCraneGameData, setShipState, setRewards],
  )

  useEffect(() => {
    window.addEventListener('message', messageHandler)

    return () => window.removeEventListener('message', messageHandler)
  }, [messageHandler])

  return (
    <GameWrapper>
      <div className="w-screen h-screen">
        {showTopbar && <HeaderNavbar route={ROUTES.GAMES} title={t('cranegame.gameTitle')} />}
        <iframe
          className="w-full h-full absolute top-0"
          id="craneIframe"
          src={playcanvasConfig.stacknloadGameURL.href}
        />
      </div>
    </GameWrapper>
  )
}
